import {
  format,
  isToday,
  isYesterday,
  isThisWeek,
  parseISO,
  startOfDay,
  endOfDay,
} from "date-fns";

const DATE_FORMAT = "MM/dd/yyyy";

// Format ex: 1:00 PM
const TIME_FORMAT = "h:mm a";

export type DateLike = Date | string;

export function isDateLike(x: unknown): x is DateLike {
  return x instanceof Date || typeof x === "string";
}

export function formatDateDay(date: DateLike): string {
  const dateObj = new Date(date);
  if (isToday(dateObj)) {
    return "Today";
  }

  if (isYesterday(dateObj)) {
    return "Yesterday";
  }

  if (isThisWeek(dateObj)) {
    // Format as 'Monday', 'Tuesday', etc.
    return format(dateObj, "eeee");
  }

  return format(dateObj, DATE_FORMAT);
}

export function formatDateDayTime(date: DateLike): string {
  const dateObj = new Date(date);

  return formatDateDay(dateObj) + ", " + format(dateObj, TIME_FORMAT);
}

export function formatDateOnly(date: DateLike): string {
  if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const [year, month, day] = date.split("-").map(Number);
    // Pad with leading zeros
    const mm = String(month).padStart(2, "0");
    const dd = String(day).padStart(2, "0");
    return `${mm}/${dd}/${year}`;
  }
  const dateObj = new Date(date);
  return format(dateObj, DATE_FORMAT);
}

export function startOfLocalDay(date: string): Date {
  return startOfDay(parseISO(date.split("T")[0]));
}

export function endOfLocalDay(date: string): Date {
  return endOfDay(parseISO(date.split("T")[0]));
}

export function validateEffectiveStartDateForProgramYear(
  value: string | undefined,
  programYear: string | undefined
): string | true {
  if (!value || value.trim() === "") {
    return "Effective Start Date is required";
  }

  if (!programYear) return true;

  const programYearMatch = programYear.match(/^(\d{4})-(\d{4})$/);
  if (!programYearMatch) return true;

  const startYear = parseInt(programYearMatch[1], 10);
  const endYear = parseInt(programYearMatch[2], 10);

  const programYearStart = new Date(startYear, 6, 1, 0, 0, 0, 0);
  const programYearEnd = new Date(endYear, 5, 30, 23, 59, 59, 999);

  let effectiveDate: Date;

  if (value.includes("/")) {
    const [month, day, year] = value.split("/").map(Number);
    effectiveDate = new Date(year, month - 1, day, 0, 0, 0, 0);
  } else if (value.includes("-")) {
    const [year, month, day] = value.split("-").map(Number);
    effectiveDate = new Date(year, month - 1, day, 0, 0, 0, 0);
  } else {
    effectiveDate = new Date(value);
    effectiveDate.setHours(0, 0, 0, 0);
  }

  if (isNaN(effectiveDate.getTime())) return true;

  if (effectiveDate < programYearStart || effectiveDate > programYearEnd) {
    return `Effective Start Date must be between 07/01/${startYear} and 06/30/${endYear} for program year ${programYear}`;
  }

  return true;
}

export function getCurrentProgramYear(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const startYear = month >= 6 ? year : year - 1;
  return `${startYear}-${startYear + 1}`;
}

export function defaultProgramYear(initial?: string): string {
  return initial ?? getCurrentProgramYear();
}
